// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0

import { Reactive, reactive } from "@conterra/reactivity-core";
import { MapModel } from "../../model/MapModel";
import {
    assertInternalConstructor,
    INTERNAL_CONSTRUCTOR_TAG,
    InternalConstructorTag
} from "../../utils/InternalConstructorTag";
import type { LayerLoadInfo, LayerLoadState } from "../AbstractLayer";
import { AbstractLayerBase } from "../AbstractLayerBase";
import {
    ATTACH_TO_MAP,
    ATTACH_TO_PARENT,
    DETACH_FROM_MAP,
    GET_RAW_SUBLAYERS,
    SET_LEGEND
} from "../shared/internals";
import { LayerBaseConfig } from "../shared/LayerConfig";
import { SublayerBaseType } from "../shared/SublayerBaseType";
import { SublayersCollection } from "../shared/SublayersCollection";
import { WMSLayer } from "../WMSLayer";

/**
 * Configuration options to construct the sublayers of a {@link WMSLayer}.
 *
 * @group Layers
 */
export interface WMSSublayerConfig extends LayerBaseConfig {
    /**
     * The name of the WMS sublayer in the service's capabilities.
     * Not mandatory, e.g. for WMS group layer. See [WMS spec](https://www.ogc.org/standard/wms/).
     */
    name?: string;

    /** Configuration for nested sublayers. */
    sublayers?: WMSSublayerConfig[];
}

export const SET_SUBLAYER_LOAD_INFO = Symbol("SET_SUBLAYER_LOAD_INFO");

/**
 * Represents a sublayer of a {@link WMSLayer}.
 *
 * @group Layers
 */
export class WMSSublayer extends AbstractLayerBase implements SublayerBaseType {
    #parent: WMSSublayer | WMSLayer | undefined;
    #parentLayer: WMSLayer | undefined;
    #name: string | undefined;
    #legend = reactive<string>();
    #sublayers: SublayersCollection<WMSSublayer>;
    #visible: Reactive<boolean>;
    #loadInfo = reactive<LayerLoadInfo>("loaded");

    /**
     * @internal
     */
    constructor(config: WMSSublayerConfig, tag: InternalConstructorTag) {
        assertInternalConstructor(tag);

        super(config);
        this.#name = config.name;
        this.#visible = reactive(config.visible ?? true);
        this.#sublayers = new SublayersCollection(
            constructSublayers(config.sublayers),
            INTERNAL_CONSTRUCTOR_TAG
        );
    }

    override get type() {
        return "wms-sublayer" as const;
    }

    /**
     * The name of the WMS sublayer in the service's capabilities.
     *
     * Is optional as a WMS group layer in a WMS service does not need to have a name.
     */
    get name(): string | undefined {
        return this.#name;
    }

    override get layers(): undefined {
        return undefined;
    }

    override get sublayers(): SublayersCollection<WMSSublayer> {
        return this.#sublayers;
    }

    override get parent(): WMSSublayer | WMSLayer {
        const parent = this.#parent;
        if (!parent) {
            throw new Error(`WMS sublayer ${this.id} has not been attached to its parent yet.`);
        }
        return parent;
    }

    get parentLayer(): WMSLayer {
        const parentLayer = this.#parentLayer;
        if (!parentLayer) {
            throw new Error(`WMS sublayer ${this.id} has not been attached to its parent yet.`);
        }
        return parentLayer;
    }

    override get legend(): string | undefined {
        return this.#legend.value;
    }

    get loadState(): LayerLoadState {
        const current = this.#loadInfo.value;
        if (typeof current === "string") {
            return current;
        }
        return current.kind;
    }

    /**
     * The error (if any) that prevented this sublayer from loading.
     */
    get loadError(): Error | undefined {
        const current = this.#loadInfo.value;
        if (typeof current === "object" && "kind" in current && current.kind === "error") {
            return current.error;
        }
        return undefined;
    }

    override get visible(): boolean {
        return this.#visible.value;
    }

    /**
     * Called by the parent layer when it is attached to the map to attach all sublayers.
     *
     * @internal
     */
    override [ATTACH_TO_MAP](map: MapModel): void {
        super[ATTACH_TO_MAP](map);

        // Recurse into nested sublayers
        for (const sublayer of this.sublayers[GET_RAW_SUBLAYERS]()) {
            sublayer[ATTACH_TO_MAP](map);
        }
    }

    /** @internal */
    override [DETACH_FROM_MAP](): void {
        super[DETACH_FROM_MAP]();
        for (const sublayer of this.#sublayers.getSublayers()) {
            sublayer[DETACH_FROM_MAP]();
        }
    }

    /**
     * Attaches this sublayer to its parent _layer_ and its immediate parent _layer or sublayer_.
     *
     * @internal
     */
    [ATTACH_TO_PARENT](parentLayer: WMSLayer, parent: WMSLayer | WMSSublayer): void {
        if (this.#parent) {
            throw new Error(
                `WMS sublayer '${this.id}' has already been attached to parent '${this.#parent.id}'`
            );
        }
        this.#parent = parent;

        if (this.#parentLayer) {
            throw new Error(
                `WMS sublayer '${this.id}' has already been attached to parent layer '${this.#parentLayer.id}'`
            );
        }
        this.#parentLayer = parentLayer;

        // Recurse into nested sublayers
        for (const sublayer of this.sublayers[GET_RAW_SUBLAYERS]()) {
            sublayer[ATTACH_TO_PARENT](parentLayer, this);
        }
    }

    /**
     * Called by the parent layer to update the legend on load.
     *
     * @internal
     */
    [SET_LEGEND](legendUrl: string | undefined) {
        this.#legend.value = legendUrl;
    }

    /**
     * Called by the parent layer once the capabilities have been validated.
     *
     * @internal
     */
    [SET_SUBLAYER_LOAD_INFO](loadInfo: LayerLoadInfo): void {
        this.#loadInfo.value = loadInfo;
    }

    override setVisible(newVisibility: boolean): void {
        this.#visible.value = newVisibility;
    }
}

/**
 * Constructs a tree of wms sublayers from a nested configuration object.
 */
export function constructSublayers(sublayerConfigs: WMSSublayerConfig[] = []): WMSSublayer[] {
    const sublayers: WMSSublayer[] = [];
    try {
        for (const sublayerConfig of sublayerConfigs) {
            sublayers.push(new WMSSublayer(sublayerConfig, INTERNAL_CONSTRUCTOR_TAG));
        }
        return sublayers;
    } catch (e) {
        // Ensure previous sublayers are destroyed if a single constructor throws
        while (sublayers.length) {
            const layer = sublayers.pop();
            layer?.destroy();
        }
        throw new Error("Failed to construct sublayers.", { cause: e });
    }
}
